import { useEffect, useRef } from 'react';
import { useSendbirdChat } from '@sendbird/uikit-react-native';
import { SessionHandler } from '@sendbird/chat';
import { SBTokenService } from 'app/auth/services/SBTokenService';
import { Telemetry } from 'app/services/telemetry/Telemetry';

const MAX_CONSECUTIVE_REFRESH_ATTEMPTS = 3;

export const useSendbirdSessionHandler = (): void => {
  const { sdk } = useSendbirdChat();
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sdk?.setSessionHandler) {
      return;
    }

    sdk.setSessionHandler(
      new SessionHandler({
        onSessionTokenRequired: (resolve, reject) => {
          if (attemptsRef.current >= MAX_CONSECUTIVE_REFRESH_ATTEMPTS) {
            Telemetry.error(new Error('Sendbird session token refresh limit reached'), {
              event: 'auth.sendbird_session.refresh_limit',
              domain: 'auth',
              context: { source: 'useSendbirdSessionHandler', attempts: attemptsRef.current },
            });
            resolve(null);
            return;
          }

          attemptsRef.current += 1;

          SBTokenService.clearToken()
            .then(() => SBTokenService.requestToken())
            .then(() => SBTokenService.getToken())
            .then(token => resolve(token || null))
            .catch(error => {
              Telemetry.error(error as Error, {
                event: 'auth.sendbird_session.refresh_failed',
                domain: 'auth',
                context: { source: 'useSendbirdSessionHandler' },
              });
              reject(error as Error);
            });
        },
        onSessionRefreshed: () => {
          attemptsRef.current = 0;
        },
        onSessionError: error => {
          Telemetry.error(error, {
            event: 'auth.sendbird_session.error',
            domain: 'auth',
            context: { source: 'useSendbirdSessionHandler' },
          });
        },
        onSessionClosed: () => {
          Telemetry.log('warning', 'Sendbird session closed', { source: 'useSendbirdSessionHandler' });
        },
      })
    );
  }, [sdk]);
};
